import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { HTTP_404_ERROR } from 'Constants'
import { useErrorHandling } from 'context/errorHandling'
import { fetchContractor, fetchContractorBalance } from 'services/ContractorService'
import { TitleBar } from 'components/Shared/CrudComponents/TitleBar'
import { Loader } from 'components/Shared/Loader'
import {
  CONTRACTOR_ADDITIONAL_DATA_READ,
  CONTRACTOR_ADDRESS_READ,
  CONTRACTOR_ATTACHMENTS_READ,
  CONTRACTOR_CORRESPONDENCE_READ,
  CONTRACTOR_DATA_READ,
  CONTRACTOR_EDIT,
  CONTRACTOR_FINANCE_DATA_READ,
  CONTRACTOR_HISTORY_READ,
  CONTRACTOR_POLICY_READ,
  useAuth
} from 'context/auth'
import Module from 'constants/customFieldModules'
import { DetailsRow } from 'components/Shared/CrudComponents'
import ContractorRole from 'constants/ContractorRole'
import { CustomFieldsProvider } from 'context/customFields'
import { ContractorBasicDetails } from './ContractorBasicDetails'
import { ContractorCompanyAddress } from './ContractorCompanyAddress'
import { ContractorCorrespondenceAddress } from './ContractorCorrespondenceAddress'
import { ContractorAdditionalDetails } from './ContractorAdditionalDetails'
import { ContractorFinanceDetails } from './ContractorFinanceDetails'
import { ContractorFiles } from './ContractorFiles'
import { ContractorInsurancePolicies } from './ContractorInsurancePolicies'
import { ContractorHistory } from './ContractorHistory'

const CONTRACTOR_BALANCE_NO_DATA = {}

export function ContractorDetails (props) {
  const [contractor, setContractor] = useState(null)
  const [contractorBalance, setContractorBalance] = useState(CONTRACTOR_BALANCE_NO_DATA)

  const contractorId = props.match.params.contractorId

  const history = useHistory()
  const { t } = useTranslation()
  const { handleError } = useErrorHandling()
  const { hasAnyPrivileges } = useAuth()

  useEffect(() => {
      fetchContractor(contractorId)
        .then(setContractor)
        .catch(error =>
          handleError(error, {
            [HTTP_404_ERROR]: 'notifications.error.contractorNotFound'
          }).then(() => history.push('/contractors'))
        )
    }, [contractorId, handleError, history]
  )

  const hasFinanceDataReadPrivileges = hasAnyPrivileges([CONTRACTOR_FINANCE_DATA_READ])
  useEffect(() => {
      if (hasFinanceDataReadPrivileges) {
        fetchContractorBalance(contractorId)
          .then(setContractorBalance)
          .catch(handleError)
      }
    }, [contractorId, hasFinanceDataReadPrivileges, handleError]
  )

  const editContractor = () => {
    history.push('/contractors/edit/' + contractorId)
  }

  const hasContractorDataReadPrivilege = hasAnyPrivileges([CONTRACTOR_DATA_READ])
  const title = hasContractorDataReadPrivilege && contractor ? contractor.fullName: t('contractor.form.title')
  return contractor
    ? (
      <>
        <TitleBar
          buttonLabel={t('contractor.form.edit')}
          onButtonClick={editContractor}
          title={title}
          isButtonDisabled={!hasAnyPrivileges([CONTRACTOR_EDIT])}
        />
        {hasAnyPrivileges([CONTRACTOR_HISTORY_READ]) && <ContractorHistory contractorId={contractorId}/>}
        <CustomFieldsProvider module={Module.Contractors} entityId={contractor.id}>
          <DetailsRow>
            <ContractorBasicDetails contractor={contractor} visible={hasContractorDataReadPrivilege}/>
            <ContractorCompanyAddress contractor={contractor} visible={hasAnyPrivileges([CONTRACTOR_ADDRESS_READ])}/>
            <ContractorCorrespondenceAddress contractor={contractor}
                                             visible={hasAnyPrivileges([CONTRACTOR_CORRESPONDENCE_READ])}/>
            <ContractorAdditionalDetails contractor={contractor}
                                         visible={hasAnyPrivileges([CONTRACTOR_ADDITIONAL_DATA_READ])}/>
            <ContractorFinanceDetails contractorBalance={contractorBalance} visible={hasFinanceDataReadPrivileges}/>
            <ContractorFiles contractor={contractor} visible={hasAnyPrivileges([CONTRACTOR_ATTACHMENTS_READ])}/>
            {contractor.roles.includes(ContractorRole.CARRIER) && (
              <ContractorInsurancePolicies contractor={contractor}
                                           visible={hasAnyPrivileges([CONTRACTOR_POLICY_READ])}/>
            )}
          </DetailsRow>
        </CustomFieldsProvider>
      </>
    )
    : <Loader/>
}
